import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, firstValueFrom, take, timeout } from 'rxjs';

/**
 * How long anything is willing to wait for MSAL to stop being mid-flight.
 *
 * Bounded on purpose. An init that never completes must degrade to "decide with
 * what we know" rather than hang the router or the HTTP pipeline forever.
 */
export const MSAL_SETTLE_TIMEOUT_MS = 10000;

/**
 * Resolves once MSAL is no longer mid-interaction.
 *
 * WHY THIS EXISTS AS A SHARED HELPER. Asking MSAL a question before it has
 * finished handling a redirect is the single most repeated defect in this
 * codebase. The interceptor learned it first: data calls went out while
 * `getAllAccounts()` was still empty, took the no-account branch, and sent no
 * bearer. The router guard had the SAME bug with a worse blast radius — see
 * user-auth.guard.ts for the reload storm it caused.
 *
 * Waiting costs nothing once settled: `inProgress$` replays its current value,
 * so a settled app resolves on the first emission.
 */
export async function msalSettled(
  broadcast: MsalBroadcastService,
  label: string
): Promise<void> {
  try {
    await firstValueFrom(
      broadcast.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        timeout(MSAL_SETTLE_TIMEOUT_MS)
      )
    );
  } catch {
    // Never block forever on an init that never completes.
    console.warn(`${label}: MSAL did not settle in time; deciding anyway`);
  }
}
